import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { rgba } from 'polished';
import React, { forwardRef } from 'react';

import { CheckboxActive } from '../../../components/icons/CheckboxActive';
import { dynamicInput } from '../../../styles/mixins';
import { typography } from '../../../styles/typography';
import type { InputVariant } from '../Input';
import { ErrorLabel } from '../Label';

export const CheckboxHost = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 8px;
  }
`;

export const CheckboxInput = styled.input`
  ${dynamicInput};
`;

export const dynamicLabel = (props: any) => css`
  position: relative;
  width: 100%;
  cursor: pointer;
  white-space: pre-wrap;
  &:hover > div {
    background-image: url('data:image/svg+xml;utf8,<svg width="16px" height="15px" viewBox="0 0 16 15" version="1.1" xmlns="http://www.w3.org/2000/svg" ><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="Assets" transform="translate(-347.000000, -1288.000000)" stroke="%231e3d5a" stroke-width="2"><polyline id="icon-check-hover" transform="translate(355.000000, 1295.000000) scale(-1, 1) rotate(-180.000000) translate(-355.000000, -1295.000000) " points="348 1295.46154 353.5 1289 362 1301"></polyline></g></g></svg>');
    background-repeat: no-repeat;
    background-size: 12px;
    background-position: center;
  }
`;

export const CheckboxContainer = styled.div<{
  align?: string;
  disabled?: boolean;
  isValid?: boolean;
  checked?: boolean;
}>`
  margin-right: 12px;
  min-width: 24px;
  width: 24px;
  height: 24px;
  border-radius: 1px;

  box-shadow: 0 0 0 1px
    ${(props) =>
      !props.disabled
        ? props.isValid === false
          ? props.theme?.colors.warning
          : rgba(props.theme?.colors.foreground ?? '', 0.3)
        : '#a0a0a0'};
  background-color: ${(props) =>
    props.checked
      ? props.disabled
        ? '#a0a0a0'
        : props.theme?.colors.primary
      : props.theme?.colors.background};

  ${(props) =>
    props.isValid === false &&
    `background-color: ${rgba(props.theme.colors.warning, 0.1)}`};
`;

const Label = styled.label<{
  checked: boolean;
  disabled?: boolean;
  align?: string;
  isValid?: boolean;
  variant: InputVariant;
}>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  ${dynamicLabel};
  ${typography.numbers2};
  color: ${({ theme }) => theme.colors.foreground};
  box-shadow: 0 0 0 1px
    ${({ variant, theme }) =>
      variant === 'fullBorder' || variant === 'bottomBorder'
        ? rgba(theme?.colors.foreground ?? '', 0.8)
        : 'transparent'};
  height: 64px;
  padding-left: 15px;
  padding-right: 15px;
  background-color: ${({ theme }) => theme.colors.background};

  ${({ theme, isValid }) =>
    isValid !== undefined &&
    isValid === false &&
    `box-shadow: 0 0 0 2px ${theme?.colors.warning}`};

  &:hover {
    ${({ variant, theme }) =>
      variant === 'fullBorder' &&
      `
        box-shadow: 0 0 0 1px ${rgba(theme?.colors.foreground ?? '', 0.5)};
      `};
    & > div {
      background-color: transparent;
    }
  }

  &:active {
    box-shadow: 0 0 0 2px ${(props) => props.theme?.shades.primary3};
  }
`;

export interface IProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  id: string;
  label: string;
  align?: 'left' | 'right';
  variant?: InputVariant;
  isValid?: boolean;
  errorMessage?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, IProps>(function Cbox(
  {
    checked,
    onChange,
    id,
    label,
    disabled,
    align = 'left',
    variant = 'fullBorder',
    errorMessage,
    ...rest
  },
  ref,
) {
  return (
    <CheckboxHost {...rest}>
      <Label
        htmlFor={id}
        checked={checked}
        disabled={disabled}
        align={align}
        isValid={rest.isValid}
        variant={variant}
      >
        <CheckboxContainer
          checked={checked}
          disabled={disabled}
          align={align}
          isValid={rest.isValid}
        >
          {checked && <CheckboxActive />}
        </CheckboxContainer>
        {label}
      </Label>
      {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
      <CheckboxInput
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(!checked)}
        ref={ref}
      />
    </CheckboxHost>
  );
});
